import { Home, CloudLightning, Droplets, Sun, FileText, Shield, Search, Wrench, ChevronRight } from "lucide-react";
import { Button } from "./ui/button";

const SERVICES = [
  {
    id: "roof-replacement",
    title: "Roof Replacement",
    description: "Complete roof replacement with premium materials and expert installation. We handle everything from permits to cleanup.",
    icon: Home,
    features: ["Premium Materials", "10-Year Workmanship Warranty", "Insurance Assistance", "Financing Available"],
    color: "emerald"
  },
  {
    id: "storm-damage",
    title: "Storm Damage Repair",
    description: "Fast response to storm damage including wind, hail, and fallen debris. We work directly with your insurance company.",
    icon: CloudLightning,
    features: ["24/7 Emergency Service", "Insurance Claim Support", "Temporary Repairs", "Full Restoration"],
    color: "amber"
  },
  {
    id: "leak-repair",
    title: "Roof Leak Repair",
    description: "Expert leak detection and repair to protect your home from water damage. We find and fix the source, not just the symptom.",
    icon: Droplets,
    features: ["Leak Detection", "Interior Damage Assessment", "Permanent Solutions", "Preventive Maintenance"],
    color: "blue"
  },
  {
    id: "solar-detach",
    title: "Solar Panel Detach & Reset",
    description: "Professional solar panel removal and reinstallation for roof repairs. We coordinate with your solar provider for warranty compliance.",
    icon: Sun,
    features: ["Certified Technicians", "System Testing", "Warranty Protection", "Coordination with Solar Co."],
    color: "orange"
  },
  {
    id: "estimate-review",
    title: "Insurance Estimate Review",
    description: "Expert analysis of your insurance estimate to ensure you receive fair compensation for your roof damage.",
    icon: FileText,
    features: ["Line-by-Line Analysis", "Missing Item Identification", "Supplement Filing", "Adjuster Meeting Support"],
    color: "purple"
  },
  {
    id: "emergency-tarp",
    title: "Emergency Roof Tarping",
    description: "Immediate temporary protection for damaged roofs. Available 24/7 to prevent further damage to your home.",
    icon: Shield,
    features: ["24/7 Availability", "Same-Day Service", "Heavy-Duty Materials", "Secure Installation"],
    color: "red"
  },
  {
    id: "inspections",
    title: "Roof Inspections",
    description: "Comprehensive roof inspections for buyers, sellers, maintenance, and insurance purposes. Detailed reports provided.",
    icon: Search,
    features: ["Detailed Reports", "Photo Documentation", "Repair Estimates", "Maintenance Plans"],
    color: "teal"
  }
];

export default function ServicesSection() {
  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const getColorClasses = (color) => {
    const colors = {
      emerald: { bg: "bg-emerald-100", text: "text-emerald-600", border: "border-emerald-200" },
      amber: { bg: "bg-amber-100", text: "text-amber-600", border: "border-amber-200" },
      blue: { bg: "bg-blue-100", text: "text-blue-600", border: "border-blue-200" },
      orange: { bg: "bg-orange-100", text: "text-orange-600", border: "border-orange-200" },
      purple: { bg: "bg-purple-100", text: "text-purple-600", border: "border-purple-200" },
      red: { bg: "bg-red-100", text: "text-red-600", border: "border-red-200" },
      teal: { bg: "bg-teal-100", text: "text-teal-600", border: "border-teal-200" }
    };
    return colors[color] || colors.emerald;
  };

  return (
    <section id="services" className="py-20 md:py-28 bg-slate-100" data-testid="services-section">
      <div className="max-w-[1400px] mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-4 border border-emerald-200">
            <Wrench className="w-4 h-4" />
            Our Services
          </div>
          <h2 className="font-heading text-4xl md:text-5xl text-slate-900 mb-4">
            Roof & Solar Solutions
          </h2>
          <p className="text-slate-600 text-lg max-w-2xl mx-auto">
            Comprehensive roofing services for DMV homeowners. From emergency repairs 
            to complete replacements, we've got you covered.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {SERVICES.map((service) => {
            const colors = getColorClasses(service.color);
            return (
              <div 
                key={service.id}
                className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm hover:shadow-lg hover:border-slate-300 transition-all duration-300 group"
                data-testid={`service-${service.id}`}
              >
                {/* Icon */}
                <div className={`w-14 h-14 ${colors.bg} rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform`}>
                  <service.icon className={`w-7 h-7 ${colors.text}`} />
                </div>

                {/* Content */}
                <h3 className="text-slate-900 font-heading text-xl mb-3">{service.title}</h3>
                <p className="text-slate-600 text-sm mb-5 leading-relaxed">{service.description}</p>

                {/* Features */}
                <div className="space-y-2 mb-5">
                  {service.features.map((feature, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className={`w-1.5 h-1.5 rounded-full ${colors.bg.replace('/20', '').replace('/10', '')}`} />
                      <span className="text-slate-700 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* CTA */}
                <Button
                  onClick={() => scrollToSection("booking")}
                  variant="ghost"
                  className={`w-full justify-between ${colors.text} hover:bg-slate-100`}
                >
                  Schedule Service
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="text-center">
          <p className="text-slate-600 mb-4">Not sure what service you need?</p>
          <Button
            onClick={() => scrollToSection("booking")}
            className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-8 py-5"
          >
            Schedule a Free Inspection
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </section>
  );
}
